<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="团队日报详情">
      <div class="absolute inset-0 bg-slate-900/40" @click="closeDrawer"></div>
      <aside class="absolute inset-y-0 right-0 flex h-full w-full max-w-4xl flex-col bg-slate-50 shadow-2xl sm:w-[92vw] lg:w-[860px]">
        <header class="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-sm font-semibold text-blue-600">
              {{ userInitials }}
            </div>
            <div class="min-w-0">
              <h2 class="truncate text-lg font-semibold text-slate-900">{{ reportTitle }}</h2>
              <p class="mt-0.5 text-sm text-slate-500">{{ detail?.reportDate || '--' }}</p>
              <div v-if="detail" class="mt-2 flex flex-wrap gap-1.5">
                <span class="status-badge bg-slate-100 text-slate-700">{{ sourceLabel }}</span>
                <span
                  v-for="badge in statusSummaryBadges"
                  :key="badge.key"
                  class="status-badge"
                  :class="badge.class"
                >
                  {{ badge.label }}
                </span>
              </div>
              <p v-if="readonlyHint" class="mt-2 text-xs leading-5 text-slate-500">{{ readonlyHint }}</p>
            </div>
          </div>
          <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭详情" aria-label="关闭详情" @click="closeDrawer">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </header>

        <nav class="shrink-0 overflow-x-auto border-b border-slate-200 bg-white px-4 sm:px-6" role="tablist" aria-label="日报详情内容">
          <div class="flex min-w-max gap-1 pb-0.5">
            <button
              v-for="tab in availableTabs"
              :key="tab.value"
              type="button"
              role="tab"
              class="rounded-t-md px-3 py-2.5 text-sm font-medium transition-all duration-200"
              :class="activeTab === tab.value ? 'bg-slate-50 text-slate-900' : 'text-slate-500 hover:text-slate-700'"
              :aria-selected="activeTab === tab.value"
              @click="activeTab = tab.value"
            >
              {{ tab.label }}
            </button>
          </div>
        </nav>

        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <div v-if="loading" class="data-table-empty is-compact">正在加载日报详情...</div>
          <template v-else-if="detail">
            <ReportComparisonPanel
              v-if="activeTab === 'comparison'"
              :report="detail"
              :versions="phaseVersions"
              :selected-version="selectedVersion"
              :loading-version="versionLoading"
              @select-version="emit('select-version', $event)"
              @open-annotations="openTodoAnnotations"
            />

            <div v-if="activeTab === 'evaluation'" class="space-y-8">
              <ReportEvaluationPanel
                v-if="canEvaluate"
                :report="detail"
                :submitting="evaluationSubmitting"
                :disabled="operationSubmitting"
                @submit="emit('evaluate', $event)"
              />
              <ReportEvaluationSummary
                v-else
                :evaluations="detail.evaluations"
              />
            </div>

            <ReportCommentPanel
              v-if="activeTab === 'comments'"
              :comments="detail.comments"
              :disabled="operationSubmitting"
              :can-submit="canComment"
              :can-request-supplement="canRequestSupplement"
              :focus-comment-id="focusCommentId"
              @submit="submitComment"
              @request-supplement="requestSupplement"
            />
          </template>
        </div>
      </aside>

      <div v-if="showTodoAnnotationModal" class="absolute inset-0 z-20 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="待办批注">
        <div class="absolute inset-0 bg-slate-900/30" @click="closeTodoAnnotationModal"></div>
        <section class="relative flex max-h-[82vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
          <header class="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <div class="min-w-0">
              <h3 class="flex items-center gap-2 text-base font-semibold text-slate-900">
                <ChatBubbleLeftRightIcon class="h-5 w-5 text-blue-500" />
                待办批注
              </h3>
              <p class="mt-1 truncate text-sm text-slate-500">{{ todoAnnotationWorkItem?.title || todoAnnotationWorkItem?.todoSnapshot?.title || '--' }}</p>
            </div>
            <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭" aria-label="关闭" @click="closeTodoAnnotationModal">
              <XMarkIcon class="h-5 w-5" />
            </button>
          </header>
          <main class="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div v-if="todoAnnotationLoading" class="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
              <div class="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
              正在加载批注...
            </div>
            <div v-else-if="todoAnnotationError" class="flex flex-col items-center gap-3 py-12 text-center">
              <ExclamationTriangleIcon class="h-8 w-8 text-rose-400" />
              <p class="text-sm text-slate-500">批注加载失败，请重试。</p>
              <button type="button" class="btn-secondary" @click="loadTodoAnnotations">重试</button>
            </div>
            <div v-else-if="!todoAnnotations.length" class="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
              暂无批注
            </div>
            <div v-else class="space-y-3">
              <div v-for="annotation in todoAnnotations" :key="annotation.id || annotation.createTime || annotation.content" class="rounded-lg border border-slate-100 bg-slate-50/70 px-3.5 py-3">
                <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                  <span class="font-medium text-slate-600">{{ annotation.creatorName || annotation.createByName || annotation.authorName || '未知用户' }}</span>
                  <span>{{ annotation.createTime || annotation.createdAt || '' }}</span>
                </div>
                <p class="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{{ annotation.content || annotation.remark || '--' }}</p>
              </div>
            </div>
          </main>
          <footer v-if="canAddTodoAnnotation" class="shrink-0 border-t border-slate-100 bg-slate-50 px-5 py-4">
            <label class="block">
              <span class="form-label">新增批注</span>
              <textarea
                v-model="todoAnnotationContent"
                class="form-textarea form-control-sm mt-1 w-full"
                rows="3"
                maxlength="1000"
                placeholder="请输入批注内容"
              ></textarea>
            </label>
            <div class="mt-3 flex justify-end gap-2">
              <button type="button" class="btn-secondary" :disabled="todoAnnotationSubmitting" @click="closeTodoAnnotationModal">取消</button>
              <button type="button" class="btn-primary" :disabled="todoAnnotationSubmitting || !todoAnnotationContent.trim()" @click="submitTodoAnnotation">
                {{ todoAnnotationSubmitting ? '提交中' : '提交批注' }}
              </button>
            </div>
          </footer>
          <footer v-else-if="todoAnnotationWorkItem?.todoId" class="shrink-0 border-t border-slate-100 bg-slate-50 px-5 py-3 text-xs text-slate-500">
            仅待办负责人或创建人可新增批注。
          </footer>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { ChatBubbleLeftRightIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { addWorkItemTodoAnnotation, listWorkItemTodoAnnotations } from '@/api/workReport'
import ReportCommentPanel from './ReportCommentPanel.vue'
import ReportComparisonPanel from './ReportComparisonPanel.vue'
import ReportEvaluationPanel from './ReportEvaluationPanel.vue'
import ReportEvaluationSummary from './ReportEvaluationSummary.vue'
import { workReportStatusMeta } from '../workReportUtils'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  detail: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  canEvaluate: { type: Boolean, default: false },
  canRequestSupplement: { type: Boolean, default: false },
  canComment: { type: Boolean, default: false },
  evaluationSubmitting: { type: Boolean, default: false },
  supplementSubmitting: { type: Boolean, default: false },
  operationSubmitting: { type: Boolean, default: false },
  supplementResetToken: { type: Number, default: 0 },
  selectedVersion: { type: Object, default: null },
  versionLoading: { type: Boolean, default: false },
  initialTab: { type: String, default: 'comparison' },
  focusCommentId: { type: [String, Number], default: '' }
})

const emit = defineEmits(['update:modelValue', 'evaluate', 'request-supplement', 'comment', 'select-version'])
const activeTab = ref('comparison')
const showTodoAnnotationModal = ref(false)
const todoAnnotationLoading = ref(false)
const todoAnnotationSubmitting = ref(false)
const todoAnnotationError = ref(false)
const todoAnnotationWorkItem = ref(null)
const todoAnnotations = ref([])
const todoAnnotationContent = ref('')
const hasComments = computed(() => props.detail?.comments?.length > 0)
const hasEvaluations = computed(() => props.detail?.evaluations?.length > 0)
const canAddTodoAnnotation = computed(() => Boolean(props.detail?.id
  && todoAnnotationWorkItem.value?.id
  && todoAnnotationWorkItem.value?.todoId
  && todoAnnotationWritable(todoAnnotationWorkItem.value)))
const shouldShowEvaluationTab = computed(() =>
  props.canEvaluate
  || hasEvaluations.value
  || props.initialTab === 'evaluation'
)
const shouldShowCommentTab = computed(() =>
  hasComments.value
  || props.canComment
  || props.canRequestSupplement
  || props.initialTab === 'comments'
  || Boolean(props.focusCommentId)
)
const detailContext = computed(() => props.detail?.detailContext || {})
const statusSummary = computed(() => props.detail?.statusSummary || {})
const phaseVersions = computed(() => Array.isArray(props.detail?.phaseVersions) ? props.detail.phaseVersions : [])
const availableTabs = computed(() => {
  const tabs = [
    { value: 'comparison', label: '日报内容' }
  ]

  if (shouldShowEvaluationTab.value) tabs.splice(1, 0, { value: 'evaluation', label: '评价' })
  if (shouldShowCommentTab.value) tabs.push({ value: 'comments', label: '评论与补充' })
  return tabs
})
const sourceLabel = computed(() => {
  if (detailContext.value.source === 'COPIED_TO_ME') return '抄送我的'
  if (detailContext.value.source === 'TEAM') return '直属团队'
  return '我的日报'
})
const readonlyHint = computed(() => detailContext.value.readOnly ? detailContext.value.viewHint || '该日报仅支持查看' : '')
const statusSummaryBadges = computed(() => {
  const summary = statusSummary.value
  const badges = [
    {
      key: 'planStatus',
      label: `计划：${statusLabel(summary.planStatus || props.detail?.planStatus)}`,
      class: statusClass(summary.planStatus || props.detail?.planStatus)
    },
    {
      key: 'summaryStatus',
      label: `总结：${statusLabel(summary.summaryStatus || props.detail?.summaryStatus)}`,
      class: statusClass(summary.summaryStatus || props.detail?.summaryStatus)
    }
  ]
  if (summary.planLate || summary.summaryLate) badges.push({ key: 'late', label: '存在迟交', class: 'bg-amber-50 text-amber-700' })
  if (summary.planMissing || summary.summaryMissing) badges.push({ key: 'missing', label: '存在缺交', class: 'bg-rose-50 text-rose-700' })
  if (summary.awaitingEvaluation) badges.push({ key: 'awaitingEvaluation', label: '待评价', class: 'bg-blue-50 text-blue-700' })
  if (summary.supplementRequested) badges.push({ key: 'supplementRequested', label: '待补充', class: 'bg-orange-50 text-orange-700' })
  return badges
})
const reportTitle = computed(() => {
  const employee = props.detail?.employee
  return props.detail?.employeeName || employee?.name || employee?.realName || '团队日报详情'
})
const userInitials = computed(() => {
  const name = reportTitle.value
  if (name === '团队日报详情') return '?'
  return name.slice(0, 2).toUpperCase()
})

watch(() => props.modelValue, (visible) => {
  if (visible) activeTab.value = props.initialTab || 'comparison'
})
watch(() => props.initialTab, (tab) => {
  if (props.modelValue && tab) activeTab.value = tab
})
watch(availableTabs, (tabs) => {
  if (props.modelValue && props.initialTab && tabs.some((tab) => tab.value === props.initialTab)) {
    activeTab.value = props.initialTab
    return
  }
  if (!tabs.some((tab) => tab.value === activeTab.value)) activeTab.value = 'comparison'
})
function closeDrawer() {
  emit('update:modelValue', false)
}

function requestSupplement(payload) {
  if (!props.canRequestSupplement) return
  const content = String(payload?.content || '')
  const normalizedContent = content.trim()
  if (!normalizedContent) return
  emit('request-supplement', {
    content: normalizedContent
  })
}

function submitComment(comment) {
  if (!props.canComment) return
  emit('comment', comment)
}

async function openTodoAnnotations(item) {
  if (!props.detail?.id || !item?.id || !item?.todoId) return
  todoAnnotationWorkItem.value = item
  todoAnnotationContent.value = ''
  showTodoAnnotationModal.value = true
  await loadTodoAnnotations()
}

async function loadTodoAnnotations() {
  const item = todoAnnotationWorkItem.value
  if (!props.detail?.id || !item?.id) return
  todoAnnotationLoading.value = true
  todoAnnotationError.value = false
  try {
    const result = await listWorkItemTodoAnnotations(props.detail.id, item.id)
    todoAnnotations.value = normalizeCollection(result)
  } catch {
    todoAnnotationError.value = true
    ElMessage.error('待办批注加载失败，请重试')
  } finally {
    todoAnnotationLoading.value = false
  }
}

function closeTodoAnnotationModal() {
  showTodoAnnotationModal.value = false
  todoAnnotationLoading.value = false
  todoAnnotationSubmitting.value = false
  todoAnnotationError.value = false
  todoAnnotationWorkItem.value = null
  todoAnnotations.value = []
  todoAnnotationContent.value = ''
}

async function submitTodoAnnotation() {
  const content = todoAnnotationContent.value.trim()
  const item = todoAnnotationWorkItem.value
  if (!content || !props.detail?.id || !item?.id || !canAddTodoAnnotation.value) return
  todoAnnotationSubmitting.value = true
  try {
    await addWorkItemTodoAnnotation(props.detail.id, item.id, { content })
    todoAnnotationContent.value = ''
    ElMessage.success('批注已同步')
    await loadTodoAnnotations()
  } catch {
    ElMessage.error('批注提交失败，请重试')
  } finally {
    todoAnnotationSubmitting.value = false
  }
}

function todoAnnotationWritable(item) {
  const writable = item?.todoAnnotationWritable
    ?? item?.todoAnnotationCanWrite
    ?? item?.canAddTodoAnnotation
    ?? item?.currentTodoSnapshot?.todoAnnotationWritable
    ?? item?.todoSnapshot?.todoAnnotationWritable
  return Boolean(writable)
}

function normalizeCollection(value) {
  if (Array.isArray(value)) return value
  return value?.records || value?.list || value?.rows || value?.data || []
}

function statusLabel(status) {
  return workReportStatusMeta(status).label
}

function statusClass(status) {
  if (status === 'SUBMITTED') return 'bg-emerald-50 text-emerald-700'
  if (status === 'MISSING') return 'bg-rose-50 text-rose-700'
  if (status === 'LATE') return 'bg-amber-50 text-amber-700'
  return 'bg-slate-100 text-slate-700'
}
</script>
